<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import { useDisplay, useTheme } from 'vuetify';
    import EventInfo from '@/pages/core/event/EventInfo.vue';
    import EventMarketsTab from '@/pages/core/event/EventMarketsTab.vue';
    import { useEventStore } from '@/stores/event';
    import { useAppStore } from '@/stores/app';
    import { BetStatus, EventFeedType, EventPeriod, MarketGroup } from '@/constants';
    import { BetItem } from '@/models/bet';
    import { feedService } from '@/services/feedService';
    import EventBetsSidePanel from '@/pages/core/event/EventBetsSidePanel.vue';
    import EventPositionSidePanel from '@/pages/core/event/EventPositionSidePanel.vue';
    import EventOrdersSidePanel from '@/pages/core/event/EventOrdersSidePanel.vue';
    import EventStatsSidePanel from '@/pages/core/event/EventStatsSidePanel.vue';
    import { betStatusDictionary } from '@/stores';
    import { useBetDialogsRefs } from '../../../composables/useBetDialogRef';

    // Stores
    const eventStore = useEventStore();
    const appStore = useAppStore();

    const theme = useTheme();
    const display = useDisplay();
    const route = useRoute();
    const { t } = useI18n();

    const tableHeight = ref(0);
    const eventMarketsTabRef = ref<any>(null);
    const betDialogsRefs = useBetDialogsRefs();

    const betsTotal = computed<BetItem[]>(() => appStore.getBetsByParams({ customEventId: eventStore.customEventId, statusId: [BetStatus.Running, BetStatus.Waiting, BetStatus.Rejected] }));

    const betsTotalSettled = computed<BetItem[]>(() => appStore.getBetsByParams({ customEventId: eventStore.customEventId, statusId: BetStatus.Settled }));

    const betsFTRegular = computed<BetItem[]>(() => {
        let bets = appStore.getBetsByParams({
            periodTypeId: EventPeriod.FullTime,
            customEventId: eventStore.customEventId,
        });
        return bets.filter((bet) => bet.marketGroup === MarketGroup.Regular);
    });

    const bets1HRegular = computed<BetItem[]>(() => {
        let bets = appStore.getBetsByParams({
            periodTypeId: EventPeriod.FirstHalf,
            customEventId: eventStore.customEventId,
        });
        return bets.filter((bet) => bet.marketGroup === MarketGroup.Regular);
    });

    const betsFTCorners = computed<BetItem[]>(() => {
        let bets = appStore.getBetsByParams({
            periodTypeId: EventPeriod.FullTime,
            customEventId: eventStore.customEventId,
        });
        return bets.filter((bet) => bet.marketGroup === MarketGroup.Corners);
    });

    const bets1HCorners = computed<BetItem[]>(() => {
        let bets = appStore.getBetsByParams({
            periodTypeId: EventPeriod.FirstHalf,
            customEventId: eventStore.customEventId,
        });
        return bets.filter((bet) => bet.marketGroup === MarketGroup.Corners);
    });

    const positionFTRegular = computed(() => (betsFTRegular.value.length ? calcPosition(betsFTRegular.value) : null));
    const position1HRegular = computed(() => (bets1HRegular.value.length ? calcPosition(bets1HRegular.value) : null));
    const positionFTCorners = computed(() => (betsFTCorners.value.length ? calcPosition(betsFTCorners.value) : null));
    const position1HCorners = computed(() => (bets1HCorners.value.length ? calcPosition(bets1HCorners.value) : null));

    const orders = computed(() => appStore.getOrdersByParams({ customEventId: eventStore.customEventId }));

    const positionHeight = computed(() => {
        let height = 0;
        if ((positionFTRegular.value || position1HRegular.value) && (positionFTCorners.value || position1HCorners.value)) {
            height = 150;
        } else if (positionFTRegular.value || position1HRegular.value || positionFTCorners.value || position1HCorners.value) {
            height = 102;
        }
        return height;
    });

    const tableHeightOrders = computed(() => {
        let height = Number(tableHeight.value) - positionHeight.value;
        if (betsTotal.value.length || betsTotalSettled.value.length) {
            const splitCount = betsTotal.value.length && betsTotalSettled.value.length ? 3 : 2;
            const gapSize = betsTotal.value.length && betsTotalSettled.value.length ? 32 : 16;
            const maxHeight = (height - gapSize) / splitCount - 34;
            const calculated = orders.value.length * 66 + 4;
            return calculated > maxHeight ? maxHeight : calculated;
        }
        return height - 34;
    });

    const tableHeightBets = computed(() => {
        let height = Number(tableHeight.value) - positionHeight.value;
        if (orders.value.length) {
            height -= 16 + tableHeightOrders.value + 68;
        } else {
            height -= 34;
        }
        if (betsTotal.value.length && betsTotalSettled.value.length) {
            const total = betsTotal.value.length * 66 + 4;
            const settled = betsTotalSettled.value.length * 66 + 4;
            if (total + settled + 16 < height) {
                height = Math.max(total, settled);
            } else {
                height = (height - 50) / 2;
            }
        }
        return height;
    });

    watch(
        () => [display.width.value, display.height.value],
        () => onResize()
    );
    watch(
        () => route.name,
        () => {
            //we need to close event popup if route changed (or user is logged out)
            cancel();
        }
    );

    onMounted(() => {
        console.log('Component %s mounted.', getCurrentInstance()?.type.__name);

        feedService.wsSubscribe();
        onResize();
    });

    onBeforeUnmount(() => {
        console.log('Component %s unmounted.', getCurrentInstance()?.type.__name);
    });

    function cancel() {
        eventStore.resetEventData();

        if (betDialogsRefs) {
            Object.entries(betDialogsRefs).forEach(([, betDialog]) => {
                if (betDialog && betDialog.isCreatedFromFeed === false) {
                    betDialog.cancel();
                }
            });
        }

        feedService.wsSubscribe();
    }

    function onResize() {
        let paddingY = display.width.value <= 940 ? 20 : 65; // - padding top and bottom 2x16

        if (display.width.value > 2560) {
            tableHeight.value = display.height.value * 0.88 - paddingY;
        } else {
            tableHeight.value = display.height.value - paddingY;
        }
    }

    // function getEventScoreForBet(bet: BetItem): { home: number | null; away: number | null } {
    //     if (eventStore.eventFeedType !== EventFeedType.Live) {
    //         return { home: 0, away: 0 };
    //     }
    //
    //     if (eventStore.stats === null) {
    //         return { home: null, away: null };
    //     }
    //     if (bet.marketGroup === MarketGroup.Corners) {
    //         if ((eventStore.stats.time !== null && eventStore.stats.time.includes('1H')) || bet.isFT()) {
    //             return { home: eventStore.stats.cornersHome, away: eventStore.stats.cornersAway };
    //         } else {
    //             //1h bet and (2nd half or unknown time)
    //             return { home: bet.cornersHomeIrHt, away: bet.cornersAwayIrHt };
    //         }
    //     } else {
    //         if ((eventStore.stats.time !== null && eventStore.stats.time.includes('1H')) || bet.isFT()) {
    //             return { home: eventStore.stats.scoreHome, away: eventStore.stats.scoreAway };
    //         } else {
    //             //1h bet and (2nd half or unknown time)
    //             return { home: bet.scoreHomeIrHt, away: bet.scoreAwayIrHt };
    //         }
    //     }
    // }

    function calcPosition(bets: BetItem[]) {
        let calculatedBetsCount = 0,
            totalBetBalance = 0;

        bets.forEach((bet) => {
            if (bet.statusId === BetStatus.Rejected || bet.statusId === BetStatus.Abnormal || bet.statusId === BetStatus.Void || bet.statusId === BetStatus.Refunded) {
                console.log(`Bet #${bet.id} position calculation is skipped due to status ${betStatusDictionary.nameById(bet.statusId)}`);
                return;
            }

            const eventCurrentScore = eventStore.getEventScoreForBet(bet);

            //possible situation when for LIVE game score not confirmed
            if (eventCurrentScore.home === null || eventCurrentScore.away === null) {
                return;
            }

            calculatedBetsCount++;
            const result = bet.calculateExpectedResult(eventCurrentScore.home, eventCurrentScore.away);

            if (result === null) {
                return;
            }

            totalBetBalance += Number(result.balance);
        });

        if (calculatedBetsCount > 0) {
            return totalBetBalance.toFixed(2);
        }

        return null;
    }

    defineExpose({ open });
</script>
<template>
    <v-dialog
        :model-value="true"
        :fullscreen="display.width.value <= 2560"
        :no-click-animation="true"
        content-class="top-dialog event-dialog"
        max-width="2560"
        persistent
        scrollable
        @keydown.esc="cancel"
    >
        <v-container :style="theme.name.value === 'dark' ? 'background-color: #404148' : 'background-color: rgb(238 238 238)'" class="fill-height pa-0" fluid>
            <v-row class="pa-0 ma-0" style="width: 100%">
                <v-col v-if="display.width.value > 940" :style="'max-height: ' + tableHeight + 'px'" class="pl-4 pr-1 pt-0 mt-3 pb-0 mb-3" style="overflow: auto">
                    <EventStatsSidePanel v-if="eventStore.isLiveStatsExist()" :tableHeight="tableHeight"></EventStatsSidePanel>
                    <EventPositionSidePanel
                        v-if="display.width.value <= 1315 && (positionFTRegular || position1HRegular || positionFTCorners || position1HCorners)"
                        ref="positionSidePanel"
                        :class="eventStore.isLiveStatsExist() ? 'mt-4' : ''"
                        :position1HCorners="position1HCorners"
                        :position1HRegular="position1HRegular"
                        :positionFTCorners="positionFTCorners"
                        :positionFTRegular="positionFTRegular"
                    ></EventPositionSidePanel>
                    <EventOrdersSidePanel
                        v-if="display.width.value <= 1315 && orders.length"
                        ref="ordersSidePanel"
                        :bets-total="betsTotal"
                        :class="eventStore.isLiveStatsExist() || positionFTRegular || position1HRegular || positionFTCorners || position1HCorners ? 'mt-4' : ''"
                        :orders="orders"
                        :tableFullHeight="tableHeight"
                        :tableHeight="tableHeightOrders"
                    ></EventOrdersSidePanel>

                    <EventBetsSidePanel
                        v-if="display.width.value <= 1315 && betsTotal.length"
                        ref="betsSidePanel"
                        :bets-total="betsTotal"
                        :class="eventStore.isLiveStatsExist() || positionFTRegular || position1HRegular || positionFTCorners || position1HCorners || orders.length ? 'mt-4' : ''"
                        :orders="orders"
                        :tableFullHeight="tableHeight"
                        :tableHeight="tableHeightBets"
                        :title="t('eventStats.openBets')"
                        :icon="mdiReceiptText"
                    ></EventBetsSidePanel>
                    <EventBetsSidePanel
                        v-if="display.width.value <= 1315 && betsTotalSettled.length"
                        ref="betsSidePanel"
                        :bets-total="betsTotalSettled"
                        :class="eventStore.isLiveStatsExist() || positionFTRegular || position1HRegular || positionFTCorners || position1HCorners || orders.length || betsTotal.length ? 'mt-4' : ''"
                        :orders="orders"
                        :tableFullHeight="tableHeight"
                        :tableHeight="tableHeightBets"
                        :title="t('eventStats.settledBets')"
                        :icon="mdiReceiptTextCheck"
                    ></EventBetsSidePanel>
                </v-col>
                <v-col :class="display.width.value <= 940 ? 'pa-0' : ''" :style="display.width.value > 1315 ? 'min-width: 50%' : 'min-width: 70%'">
                    <v-card
                        :style="eventStore.eventFeedType === EventFeedType.Live ? 'background-color: #fff9f3;min-height:' + tableHeight + 'px' : 'min-height:' + tableHeight + 'px'"
                        class="mx-auto"
                        elevation="0"
                        style="overflow: auto; border-radius: 8px"
                    >
                        <EventInfo ref="eventInfo" @close-event-dialog="cancel"></EventInfo>
                        <v-container class="pa-0">
                            <EventMarketsTab ref="eventMarketsTabRef" :tableHeight="tableHeight"></EventMarketsTab>
                        </v-container>
                    </v-card>
                </v-col>
                <v-col v-if="display.width.value > 1315" class="pl-1 pr-4">
                    <EventPositionSidePanel
                        v-if="positionFTRegular || position1HRegular || positionFTCorners || position1HCorners"
                        ref="positionSidePanel"
                        :position1HCorners="position1HCorners"
                        :position1HRegular="position1HRegular"
                        :positionFTCorners="positionFTCorners"
                        :positionFTRegular="positionFTRegular"
                    ></EventPositionSidePanel>
                    <EventOrdersSidePanel
                        v-if="orders.length"
                        ref="ordersSidePanel"
                        :bets-total="betsTotal"
                        :class="positionFTRegular || position1HRegular || positionFTCorners || position1HCorners ? 'mt-4' : ''"
                        :orders="orders"
                        :tableFullHeight="tableHeight"
                        :tableHeight="tableHeightOrders"
                    ></EventOrdersSidePanel>
                    <EventBetsSidePanel
                        v-if="betsTotal.length"
                        ref="betsSidePanel"
                        :bets-total="betsTotal"
                        :class="positionFTRegular || position1HRegular || positionFTCorners || position1HCorners || orders.length ? 'mt-4' : ''"
                        :orders="orders"
                        :tableFullHeight="tableHeight"
                        :tableHeight="tableHeightBets"
                        :title="t('eventStats.openBets')"
                        :icon="mdiReceiptText"
                    ></EventBetsSidePanel>
                    <EventBetsSidePanel
                        v-if="betsTotalSettled.length"
                        ref="betsSidePanel"
                        :bets-total="betsTotalSettled"
                        :class="positionFTRegular || position1HRegular || positionFTCorners || position1HCorners || orders.length ? 'mt-4' : ''"
                        :orders="orders"
                        :tableFullHeight="tableHeight"
                        :tableHeight="tableHeightBets"
                        :title="t('eventStats.settledBets')"
                        :icon="mdiReceiptTextCheck"
                    ></EventBetsSidePanel>
                </v-col>
            </v-row>
            <!--        <v-btn-->
            <!--            v-if="display.width.value <= 2560"-->
            <!--            tag="a"-->
            <!--            fab-->
            <!--            absolute-->
            <!--            right-->
            <!--            icon-->
            <!--            @click="cancel"-->
            <!--            style=""-->
            <!--            :style="display.width.value <= 430 ? 'top: 0px; right: 0px' : 'top: 15px'"-->
            <!--        >-->
            <!--            <v-icon :icon="mdiClose" />-->
            <!--        </v-btn>-->

            <div id="tooltip-event" class="tooltip"></div>
        </v-container>
    </v-dialog>
</template>
<style scoped src="./style.css"></style>
